import { applyDocumentChange, isPlainObject, managedConfigDocumentChanges, pointerToken, pointersOverlap, valueAtPointer } from "../pages/agentPolicy/documentDiff.js";

export const PROPOSAL_FORMAT = "control-proposal-v1";
export const PROPOSAL_AGENTS = ["claude-code", "codex", "github-copilot"];
const MAX_EDITS = 100;

export function emptyProposal() {
    return { format: PROPOSAL_FORMAT, revision: 0, edits: {}, explanations: {} };
}

export function proposalChanges(proposal, agentId) {
    return proposal.edits[agentId] || [];
}

export function proposalDocument(proposal, today, agentId) {
    return proposalChanges(proposal, agentId).reduce((doc, edit) => applyDocumentChange(doc, edit), structuredClone(today[agentId]?.document || {}));
}

export function proposalAgents(proposal) {
    return PROPOSAL_AGENTS.filter((agentId) => proposalChanges(proposal, agentId).length);
}

function checkedPointer(pointer, catalog) {
    if (typeof pointer !== "string" || !pointer.startsWith("/") || pointer === "/" || pointer.length > 500
        || pointer.split("/").some((part) => ["__proto__", "constructor", "prototype"].includes(part.replace(/~1/g, "/").replace(/~0/g, "~")))) {
        throw new Error("Choose a supported setting pointer.");
    }
    if (!catalog?.settings?.some((entry) => entry.pointer === pointer)) throw new Error(`The current catalog does not support ${pointer}. Read the available settings first.`);
}

// A new object container is not itself a setting (for example /autoMode).
// Keep edits at catalog boundaries so review and dispatch use the same keys.
function settingChanges(before, after, catalog) {
    const accepted = new Set(catalog.settings.map((entry) => entry.pointer));
    const expand = (change) => {
        if (accepted.has(change.pointer)) return [change];
        const owner = [...accepted].filter((pointer) => change.pointer.startsWith(`${pointer}/`)).sort((a, b) => b.length - a.length)[0];
        if (owner) {
            const left = valueAtPointer(before, owner), right = valueAtPointer(after, owner);
            return [{ pointer: owner, beforeFound: left.found, afterFound: right.found, before: left.value, after: right.value }];
        }
        const left = isPlainObject(change.before) ? change.before : {};
        const right = isPlainObject(change.after) ? change.after : {};
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        if (!keys.size) {
            if (isPlainObject(change.before) || isPlainObject(change.after)) return [];
            throw new Error(`The current catalog does not support ${change.pointer}.`);
        }
        return [...keys].flatMap((key) => {
            const beforeFound = change.beforeFound && Object.hasOwn(left, key), afterFound = change.afterFound && Object.hasOwn(right, key);
            if (beforeFound === afterFound && JSON.stringify(left[key]) === JSON.stringify(right[key])) return [];
            return expand({ pointer: `${change.pointer}/${pointerToken(key)}`, beforeFound, afterFound, before: left[key], after: right[key] });
        });
    };
    return [...new Map(managedConfigDocumentChanges(before, after).flatMap(expand).map((change) => [change.pointer, change])).values()];
}

// Both authors submit setting operations. Wizard navigation never enters this model.
export function editProposal(proposal, today, operations, expectedRevision) {
    if (expectedRevision !== proposal.revision) throw new Error("The draft changed. Read it again before editing.");
    if (!Array.isArray(operations) || !operations.length || operations.length > MAX_EDITS) throw new Error("Submit between 1 and 100 setting changes.");
    const next = structuredClone(proposal);
    const documents = {};
    for (const operation of operations) {
        const { agentId, pointer, value, reason = "" } = operation;
        if (!PROPOSAL_AGENTS.includes(agentId) || !today[agentId]) throw new Error("This agent type is unavailable.");
        checkedPointer(pointer, today[agentId].catalog);
        if (!["set", "remove", "reset"].includes(operation.operation)) throw new Error("Use set, remove, or reset.");
        if (typeof reason !== "string" || reason.length > 2000) throw new Error("Keep each explanation within 2,000 characters.");
        if (operation.operation === "set" && value === undefined) throw new Error("A set operation needs a value.");
        const baseline = today[agentId].document || {};
        documents[agentId] ||= proposalDocument(next, today, agentId);
        const original = valueAtPointer(baseline, pointer);
        const reset = operation.operation === "reset";
        documents[agentId] = applyDocumentChange(documents[agentId], { pointer,
            afterFound: reset ? original.found : operation.operation === "set", after: reset ? original.value : value });
        next.explanations[agentId] ||= {};
        if (reason) next.explanations[agentId][pointer] = reason;
    }
    for (const [agentId, document] of Object.entries(documents)) {
        next.edits[agentId] = settingChanges(today[agentId].document || {}, document, today[agentId].catalog);
        for (const pointer of Object.keys(next.explanations[agentId] || {})) {
            if (!next.edits[agentId].some((edit) => pointersOverlap(edit.pointer, pointer))) delete next.explanations[agentId][pointer];
        }
    }
    if (Object.values(next.edits).flat().length > MAX_EDITS || JSON.stringify(next).length > 200_000) throw new Error("The proposal exceeds the supported size. Prepare fewer changes.");
    next.revision += 1;
    return next;
}

export function baselineConflicts(proposal, today) {
    return proposalAgents(proposal).flatMap((agentId) => proposalChanges(proposal, agentId).filter((edit) => {
        const current = valueAtPointer(today[agentId]?.document || {}, edit.pointer);
        return current.found !== edit.beforeFound || JSON.stringify(current.value) !== JSON.stringify(edit.before);
    }).map((edit) => ({ agentId, pointer: edit.pointer })));
}

export function rebaseProposal(proposal, today) {
    const next = structuredClone(proposal);
    for (const agentId of proposalAgents(next)) next.edits[agentId] = settingChanges(today[agentId].document || {}, proposalDocument(next, today, agentId), today[agentId].catalog);
    next.revision += 1;
    return next;
}
